/**
 * RAV4 액세서리/컬러 우측 레일 메뉴 (mock-accessories B안 이식).
 *
 * 순수 DOM 헬퍼 — Three.js/도구 로직 없음. 액세서리 도구가 onToggle/onPanelToggle/onFocus 콜백을 주입한다.
 * 레일 = 원형 런처 2개(컬러 / Accessories) + 각 패널. 한 번에 한 패널만 열림(상호배타).
 *  - 컬러 패널: paint 도구가 만든 #paint-sidebar 를 그대로 이관해 담는다(스와치 기능 보존).
 *  - Accessories 패널: 항목 썸네일 + 개별 토글 스위치(장착 on/off). 패널 열림/닫힘은 카메라만 이동(overview/base).
 *    항목 행 클릭 = 카메라 포커스. 스위치 클릭 = 장착 토글(카메라 무관, 이벤트 분리).
 *
 * CSS 는 test-rav4.html 인라인 (mock-accessories 톤). 여기선 클래스만 붙인다.
 */

export interface AccessoryInfo {
  price: string;
  copy?: string;
  madeIn: string;
  material: string;
}

export interface AccessoryMenuItem {
  id: string;
  label: string;    // 한글
  labelEn: string;  // 영문
  thumb: string;    // BASE_URL 기준 상대 경로 (assets/accessories/<id>.webp)
  info?: AccessoryInfo;
}

export interface AccessoryMenuDeps {
  items: AccessoryMenuItem[];
  /** paint 도구가 만든 스와치 루트(#paint-sidebar). 컬러 패널로 append(이관). */
  paintPanelEl: HTMLElement;
  /** 항목 개별 장착 on/off (토글 스위치). 카메라는 안 건드림. */
  onToggle: (id: string, on: boolean) => void;
  /** 패널 열림/닫힘 → 카메라만 (overview/base). visibility 불변. */
  onPanelToggle: (open: boolean) => void;
  /** 항목 카메라로 스프링암 이동. */
  onFocus: (id: string) => void;
  /** 선택된 항목 재클릭(해제) → 전체(overview) 복귀. 없으면 무동작. */
  onOverview?: () => void;
}

const COLOR_ICON = `<svg viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"><path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"/></svg>`;
const ACC_ICON = `<svg viewBox="0 0 24 24" width="21" height="21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"><path d="M20 7h-9M14 17H5"/><circle cx="17" cy="17" r="3"/><circle cx="7" cy="7" r="3"/></svg>`;

type Menu = 'color' | 'acc' | null;

export function createAccessoryMenu(deps: AccessoryMenuDeps): void {
  const { items, paintPanelEl, onToggle, onPanelToggle, onFocus, onOverview } = deps;
  const base = import.meta.env.BASE_URL;
  const equipped = new Set<string>();                       // 장착 상태 SSOT

  const rail = document.createElement('div');
  rail.id = 'menu-rail';

  // ── 런처 2개 ──
  const mkLauncher = (id: string, aria: string, iconSvg: string, labelText: string): { el: HTMLElement; icon: HTMLElement } => {
    const el = document.createElement('div');
    el.className = 'launcher';
    el.id = id;
    el.setAttribute('role', 'button');
    el.tabIndex = 0;
    el.setAttribute('aria-label', aria);
    el.setAttribute('aria-expanded', 'false');
    const icon = document.createElement('div');
    icon.className = 'l-icon';
    icon.innerHTML = iconSvg;
    const label = document.createElement('div');
    label.className = 'l-label';
    label.textContent = labelText;
    el.append(icon, label);
    return { el, icon };
  };
  const color = mkLauncher('color-launcher', '자동차 컬러', COLOR_ICON, '컬러');
  const acc = mkLauncher('acc-launcher', 'Accessories', ACC_ICON, 'Accessories');

  const launchers = document.createElement('div');
  launchers.className = 'rail-launchers';
  launchers.append(color.el, acc.el);

  // ── 패널 2개 ──
  const colorPanel = document.createElement('div');
  colorPanel.className = 'panel';
  colorPanel.id = 'color-panel';
  const colorTitle = document.createElement('div');
  colorTitle.className = 'panel-title';
  colorTitle.textContent = '차량 컬러';
  colorPanel.append(colorTitle, paintPanelEl); // paint 스와치 이관

  const accPanel = document.createElement('div');
  accPanel.className = 'panel';
  accPanel.id = 'acc-panel';
  const accTitle = document.createElement('div');
  accTitle.className = 'panel-title';
  accTitle.textContent = 'Accessories';
  accPanel.appendChild(accTitle);

  // ── 액세서리 항목 ──
  const itemEls = new Map<string, HTMLElement>();
  for (const it of items) {
    const item = document.createElement('div');
    item.className = 'acc-item';
    item.dataset.id = it.id;
    item.innerHTML = `
      <div class="thumb" style="background-image:url('${base}${it.thumb}')"></div>
      <div class="meta"><div class="kr" id="acc-name-${it.id}">${it.label}</div><div class="en">${it.labelEn}</div></div>`;

    // ── 토글 스위치 (장착 on/off) — 카메라와 분리(stopPropagation). role=switch + aria-checked. ──
    const sw = document.createElement('button');
    sw.type = 'button';
    sw.className = 'acc-switch';
    sw.setAttribute('role', 'switch');
    sw.setAttribute('aria-checked', 'false');
    sw.setAttribute('aria-labelledby', `acc-name-${it.id}`);
    const toggle = (): void => {
      const on = !equipped.has(it.id);
      if (on) equipped.add(it.id); else equipped.delete(it.id);
      sw.setAttribute('aria-checked', String(on));   // 스위치 시각 상태 SSOT (CSS 는 [aria-checked] 로 구동)
      item.classList.toggle('equipped', on);         // 라벨 흐림/진함 연동
      onToggle(it.id, on);
    };
    // 스위치 조작은 행의 카메라(selectAcc)로 버블링되면 안 됨 → stopPropagation.
    sw.addEventListener('click', (e) => { e.stopPropagation(); toggle(); });
    sw.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); toggle(); }
    });

    item.append(sw);
    item.addEventListener('click', () => selectAcc(it.id)); // 행 본체 = 카메라 포커스
    accPanel.appendChild(item);
    itemEls.set(it.id, item);
  }

  // ── 좌측 인포 카드 (항목 선택 시 등장) ──
  const infoCard = document.createElement('div');
  infoCard.id = 'acc-info';
  const itemById = new Map(items.map((i) => [i.id, i]));
  const esc = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  function showInfo(id: string): void {
    const it = itemById.get(id);
    if (!it || !it.info) { hideInfo(); return; }
    const { price, copy, madeIn, material } = it.info;
    infoCard.innerHTML = `
      <div class="ai-title">
        <span class="ai-kr">${esc(it.label)}</span>
        <span class="ai-en">${esc(it.labelEn)}</span>
      </div>
      ${copy ? `<p class="ai-copy">${esc(copy)}</p>` : ''}
      <div class="ai-price">${esc(price)}</div>
      <div class="ai-spec">${esc(material)} · ${esc(madeIn)}</div>`;
    infoCard.classList.add('show');
  }
  function hideInfo(): void { infoCard.classList.remove('show'); }

  function selectAcc(id: string): void {
    // 이미 선택된 항목 재클릭 → 선택 해제 → 전체(overview) 복귀.
    if (itemEls.get(id)?.classList.contains('active')) {
      for (const el of itemEls.values()) el.classList.remove('active');
      hideInfo();
      onOverview?.();
      return;
    }
    for (const [k, el] of itemEls) el.classList.toggle('active', k === id);
    onFocus(id);
    showInfo(id);
  }

  rail.append(launchers, colorPanel, accPanel);
  document.getElementById('app')!.appendChild(rail);
  document.getElementById('app')!.appendChild(infoCard);

  // ── 상호배타 open/close ──
  const current = (): Menu =>
    colorPanel.classList.contains('open') ? 'color' :
    accPanel.classList.contains('open') ? 'acc' : null;

  function openMenu(menu: Menu): void {
    const wantColor = menu === 'color';
    const wantAcc = menu === 'acc';
    // 모바일: 시트 열림 동안 무대(#app)를 위로 올림 — 하단 시트/인포바가 차를 안 가리게 (CSS 효과는 모바일 BP 한정).
    document.body.classList.toggle('m-sheet-open', menu !== null);
    colorPanel.classList.toggle('open', wantColor);
    accPanel.classList.toggle('open', wantAcc);
    color.el.classList.toggle('active', wantColor);
    acc.el.classList.toggle('active', wantAcc);
    color.el.setAttribute('aria-expanded', String(wantColor));
    acc.el.setAttribute('aria-expanded', String(wantAcc));
    // 패널 열림/닫힘 → 카메라만(overview/base). 장착(스위치) 상태는 유지.
    onPanelToggle(wantAcc);
    if (!wantAcc) {
      for (const el of itemEls.values()) el.classList.remove('active');
      hideInfo(); // 패널 닫힘/컬러 전환 → 포커스/인포만 소멸(장착은 그대로).
    }
  }
  const toggleMenu = (menu: Exclude<Menu, null>): void => openMenu(current() === menu ? null : menu);

  // ── 모바일: 시트 밖 탭으로 닫기 (드래그=카메라 조작은 유지) ──
  // BP 는 CSS 하단시트와 동일 조건(matchMedia)으로 공유 — 데스크톱에선 무동작.
  // 탭 = 짧은 터치 + 이동 적음. pointerdown 위치/시각 기록 → up 에서 거리·시간 판정.
  // 닫기는 openMenu(null) 경로를 타서 카메라(onPanelToggle)만 일관되게 base 로 복귀됨(장착 상태는 유지).
  const mobileMq = window.matchMedia('(max-width: 768px), (pointer: coarse)');
  const TAP_MOVE_PX = 10;   // 이 이상 움직이면 드래그(회전)로 간주
  const TAP_TIME_MS = 500;  // 이 이상 누르면 롱프레스/드래그로 간주
  let downX = 0, downY = 0, downT = 0;
  const insideMenu = (t: EventTarget | null): boolean =>
    t instanceof Node && (rail.contains(t) || infoCard.contains(t));
  document.addEventListener('pointerdown', (e: PointerEvent) => {
    downX = e.clientX; downY = e.clientY; downT = e.timeStamp;
  }, true);
  document.addEventListener('pointerup', (e: PointerEvent) => {
    if (!mobileMq.matches || current() === null) return;
    if (insideMenu(e.target)) return; // 메뉴 내부 조작은 제외
    const moved = Math.hypot(e.clientX - downX, e.clientY - downY);
    if (moved <= TAP_MOVE_PX && e.timeStamp - downT <= TAP_TIME_MS) openMenu(null);
  }, true);

  // 클릭 마이크로 인터랙션 — 아이콘 살짝 커졌다 복귀(패널 전개와 함께).
  const bump = (icon: HTMLElement): void => {
    icon.classList.remove('bump');
    void icon.offsetWidth; // reflow 로 애니메이션 재시작
    icon.classList.add('bump');
  };
  const wire = (launcher: { el: HTMLElement; icon: HTMLElement }, menu: Exclude<Menu, null>): void => {
    const act = (): void => { bump(launcher.icon); toggleMenu(menu); };
    launcher.el.addEventListener('click', act);
    launcher.el.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); act(); }
    });
  };
  wire(color, 'color');
  wire(acc, 'acc');
}
